import { useContext, useState, useRef } from "react";
import Card from "./Card";
import { TbCirclePlus, TbEye } from "react-icons/tb";
import { YearContext, AuthContext } from "../../context/context";
import animation from "../../Animations/wish-animation.json";
import WishCard from "./CardWish";
import AddWishForm from "../Forms/AddWishForm";
import Popup from "../Elements/Popup";
import { addWish } from "../../api";
import { useLanguage } from "../../context/LanguageContext";
import PropTypes from "prop-types";

const CardWishlist = ({ showTitle = true, showPrice = true, showViewAllLink = false, cardClassName, useMaxHeight }) => {
  const { selectedYear } = useContext(YearContext);
  const { userData, setUserData } = useContext(AuthContext);
  const { copy } = useLanguage();

  const [popupType, setPopupType] = useState(null);
  const openPopup = (type) => setPopupType(type);
  const closePopup = () => setPopupType(null);

  const formRef = useRef(null);

  const handleSubmitWish = async (e) => {
    e.preventDefault();

    const form = formRef.current;

    const wishData = new FormData(form);
    wishData.append("user_id", userData.id);
    wishData.append("year", selectedYear);

    try {
      const response = await addWish(wishData, userData.id);

      if (!response || !response.success) {
        console.error("Failed to add wish");
        return;
      }

      const newWish = response.wish;

      // Add the new wish directly to the user's wishlist, ensuring it's at the top
      setUserData((prevUserData) => ({
        ...prevUserData,
        wishlists: [newWish, ...prevUserData.wishlists],
      }));

      console.log("Wish added successfully:", newWish);
      closePopup();
    } catch (err) {
      console.error("Error submitting wish:", err.message);
    }
  };

  // Filter wishlist based on selected year
  const wishData = selectedYear
    ? userData.wishlists.filter((item) => item.year === selectedYear)
    : userData.wishlists;

  // Sort the wish data to ensure status: true items come last
  const sortedWishData = wishData.sort((a, b) => {
    if (a.status === true && b.status !== true) {
      return 1; // 'a' comes after 'b'
    }
    if (a.status !== true && b.status === true) {
      return -1; // 'a' comes before 'b'
    }
    return 0; // If both have the same status, no change
  });

  const wishlistDropdown = [
    {
      text: copy.dropdown.add,
      onClick: () => openPopup("add-wish"),
      icon: <TbCirclePlus />,
    },
    { text: copy.dropdown.view_all, link: "/wishlist", icon: <TbEye /> },
  ];

  return (
    <>
      {sortedWishData && sortedWishData.length > 0 ? (
        <Card
          title={copy.card_wishlist.headline}
          description={copy.card_wishlist.description}
          options={wishlistDropdown}
          useMaxHeight={useMaxHeight !== undefined ? useMaxHeight : true}
          cardClassName={cardClassName}
        >
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 overflow-auto max-h-full hide-scrollbar">
            {sortedWishData.map((wishlistItem) => (
              <WishCard
                key={wishlistItem.id}
                wishlistItem={wishlistItem}
                showTitle={showTitle}
                showPrice={showPrice}
              />
            ))}
            {showViewAllLink && (
              <a
                href="/wishlist"
                className="w-full relative flex flex-col gap-2 transition-all duration-300"
              >
                <div className="relative w-full h-[165px] card-base-border rounded-md overflow-hidden flex items-center justify-center bg-accent-light hover:shadow-lg">
                  <span className="flex flex-col items-center text-accent text-md">
                    <TbEye className="mb-1 text-2xl" />
                    {copy.dropdown.view_all}
                  </span>
                </div>
              </a>
            )}
          </div>
        </Card>
      ) : (
        <Card
          title={copy.card_wishlist.headline}
          description={copy.card_wishlist.description}
          options={wishlistDropdown}
          lottieAnimation={animation}
          startButton={copy.dropdown.add}
          onClick={() => openPopup("add-wish")}
          className="justify-center"
          cardClassName={cardClassName || "h-full"}
          useMaxHeight={useMaxHeight}
        />
      )}

      <Popup
        isOpen={popupType === "add-wish"}
        onClose={closePopup}
        title={copy.card_wishlist.popup.add_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.add}
        onClickPrimaryLink={handleSubmitWish}
      >
        <AddWishForm handleSubmitWish={handleSubmitWish} formRef={formRef} />
      </Popup>
    </>
  );
};

CardWishlist.propTypes = {
  showTitle: PropTypes.bool,
  showPrice: PropTypes.bool,
};

export default CardWishlist;
